import React from 'react';
import { useTranslation } from 'react-i18next';

import { isDefined } from '../../../../../../../../utils/variables';
import { SkyMapExtrasUnitType } from '../../../../../../../../viewer/elements/design/skymap-extras';
import { LabelledContainer } from '../../../../../../labelled-container/labelled-container';
import { Select } from '../../../../../../select/select';
import { Stack } from '../../../../../../stack/stack';
import { TextBox } from '../../../../../../text-box/text-box';

type Props = {
  unit?: SkyMapExtrasUnitType | string;
  setUnit: (unit: string) => void;
};

type UnitType = {
  id: string;
  name: string;
  unitType: SkyMapExtrasUnitType | undefined;
};

const customUnitId = '-1';

export const UnitValues = (props: Props) => {
  const { t } = useTranslation();
  const [selectedUnitId, setSelectedUnitId] = React.useState<string>();
  const [customUnit, setCustomUnit] = React.useState<string>('');

  const unitTypes: UnitType[] = React.useMemo(
    () => [
      {
        id: '0',
        name: t('section.attributes.attributeUnitTypeSection.unit.none', {
          ns: 'skyviewDesign',
        }),
        unitType: 'none',
      },
      {
        id: '1',
        name: t('section.attributes.attributeUnitTypeSection.unit.timestamp', {
          ns: 'skyviewDesign',
        }),
        unitType: 'timestamp',
      },
      {
        id: '2',
        name: t('section.attributes.attributeUnitTypeSection.unit.meter', {
          ns: 'skyviewDesign',
        }),
        unitType: 'm',
      },
      {
        id: '3',
        name: t('section.attributes.attributeUnitTypeSection.unit.millimeter', {
          ns: 'skyviewDesign',
        }),
        unitType: 'mm',
      },
      {
        id: '4',
        name: t('section.attributes.attributeUnitTypeSection.unit.kilogram', {
          ns: 'skyviewDesign',
        }),
        unitType: 'kg',
      },
      {
        id: '5',
        name: t('section.attributes.attributeUnitTypeSection.unit.gram', {
          ns: 'skyviewDesign',
        }),
        unitType: 'g',
      },
      {
        id: '6',
        name: t('section.attributes.attributeUnitTypeSection.unit.diameter', {
          ns: 'skyviewDesign',
        }),
        unitType: 'Ø',
      },
      {
        id: customUnitId,
        name: t('section.attributes.attributeUnitTypeSection.unit.other', {
          ns: 'skyviewDesign',
        }),
        unitType: undefined,
      },
    ],
    [t],
  );

  React.useEffect(() => {
    if (!isDefined(props.unit)) {
      setSelectedUnitId('0');
      return;
    }

    const selectedUnit = unitTypes.find((x) => x.unitType === props.unit);
    if (isDefined(selectedUnit)) {
      setSelectedUnitId(selectedUnit.id);
    } else {
      setCustomUnit(props.unit);
      setSelectedUnitId(customUnitId);
    }
  }, [props, unitTypes]);

  return (
    <LabelledContainer
      text={t('section.attributes.attributeUnitTypeSection.title', { ns: 'skyviewDesign' })}
    >
      <Stack direction="column" spacing={0.2}>
        <Select
          options={unitTypes}
          padding={0.2}
          value={selectedUnitId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedUnitId(id);

            const unit = unitTypes.find((x) => x.id === id)?.unitType;
            if (isDefined(unit)) {
              props.setUnit(unit);
            } else {
              props.setUnit(customUnit);
            }
          }}
        />
        {selectedUnitId === customUnitId && (
          <TextBox
            value={customUnit}
            onChange={(e) => {
              setCustomUnit(e.target.value);
              props.setUnit(e.target.value);
            }}
          />
        )}
      </Stack>
    </LabelledContainer>
  );
};
